import React from 'react'

import type { OpenChangeReason, ReferenceType } from '@floating-ui/react'
import {
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingPortal,
  FloatingTree,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react'
import styled from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

import { IconButton } from '@components/ui/Buttons'
import { CustomDialogActions, CustomDialogBody } from '@components/ui/Dialogs'
import { DIALOG_SIZES } from '@components/ui/Dialogs/BaseDialog'
import type { CustomDialogActionsProps } from '@components/ui/Dialogs/styles'
import { XmarkRegularIcon } from '@components/ui/svgIcons'
import { Typography } from '@components/ui/Typography'

import { DIALOG_PADDING } from '@constants'
import { FLOATING_TRANSITION_DURATION } from '@constants/theme'

const SlideOverOverlay = styled(FloatingOverlay)`
  background-color: ${({ theme }) => applyOpacity(theme.colors.gray[100], 50)};
  opacity: 0;
  transition: opacity ${FLOATING_TRANSITION_DURATION}ms ease-in-out;
  z-index: ${({ theme }) => theme.zIndex.dialog};

  &[data-status='open'] {
    opacity: 1;
  }
`

const SlideOverPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  width: min(${DIALOG_SIZES.xs}, 100%);
  background-color: ${({ theme }) => theme.colors.gray[0]};
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  box-shadow: ${({ theme }) => theme.shadows[100]};
  outline: none;
  overflow: hidden;
  transform: translateX(100%);
  opacity: 0;
  transition:
    transform ${FLOATING_TRANSITION_DURATION}ms ease-in-out,
    opacity ${FLOATING_TRANSITION_DURATION / 2}ms ease;
  z-index: ${({ theme }) => theme.zIndex.dialog};
  position: fixed;
  inset: 0 0 0 auto;

  &[data-status='open'] {
    transform: translateX(0);
    opacity: 1;
  }

  &[data-status='close'] {
    transition:
      transform ${FLOATING_TRANSITION_DURATION}ms ease-in-out,
      opacity ${FLOATING_TRANSITION_DURATION / 2}ms ease ${FLOATING_TRANSITION_DURATION / 2}ms;
  }

  @media (max-width: ${DIALOG_SIZES.xs}) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`

const SlideOverHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: ${DIALOG_PADDING}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[110]};
  flex-shrink: 0;
`

/** Building block for a `skipBodyWrapper` SlideOver. Primary scrollable area. */
export const SlideOverBody = styled(CustomDialogBody)`
  flex: 1;
`

const SlideOverActionsDiv = styled(CustomDialogActions)`
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  > div {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* Holds the last group at the trailing edge, so a right-only footer isn't left aligned. */
  > div[data-pos='right'] {
    margin-left: auto;
  }
`

interface SlideOverActionsProps extends Omit<CustomDialogActionsProps, 'children'> {
  left?: React.ReactNode
  right?: React.ReactNode
}

/** Actions area below the body, with the two groups pushed to the opposite edges. */
function SlideOverActions({ left, right, ...rest }: SlideOverActionsProps) {
  if (!left && !right) {
    return null
  }

  return (
    <SlideOverActionsDiv {...rest}>
      {left && <div>{left}</div>}
      {right && <div data-pos="right">{right}</div>}
    </SlideOverActionsDiv>
  )
}

export interface TriggerProps {
  ref: ((node: ReferenceType | null) => void) & ((node: ReferenceType | null) => void)
  onClick: (event: React.MouseEvent) => void
  type: 'button'
}

export interface OnOpenChangeCallbackProps {
  nextOpen: boolean
  event: Event | undefined
  reason: OpenChangeReason | undefined
  /** Transition duration in milliseconds until the SlideOver is fully visible or hidden */
  transitionDuration: number
}

interface CommonSlideOverProps {
  children: React.ReactNode
  footerLeft?: React.ReactNode
  footerRight?: React.ReactNode
  onOpenChangeCallback?: (params: OnOpenChangeCallbackProps) => void
  /**
   * Render `children` straight into the panel instead of wrapping them in the scrollable body — compose
   * with `SlideOverBody` and an own actions area. Needed when one element (e.g. a `<form>`) has to wrap
   * both the content and the actions, so use the `footer*` props instead unless that's the case.
   */
  skipBodyWrapper?: boolean
  title: React.ReactNode
  /** Needs to be a ReactElement that accepts a ref and an onClick function */
  trigger?: React.ReactElement<TriggerProps>
}

export interface ControlledSlideOverProps extends CommonSlideOverProps {
  open: boolean
  /** SlideOver's own onOpenChange extended with transitionDuration being sent back */
  onOpenChange: (open: boolean, event?: Event, reason?: OpenChangeReason, transitionDuration?: number) => void
}

export interface UncontrolledSlideOverProps extends CommonSlideOverProps {
  open?: undefined
  onOpenChange?: undefined
}

export type SlideOverProps = ControlledSlideOverProps | UncontrolledSlideOverProps

function SlideOverComponent({
  children,
  footerLeft,
  footerRight,
  onOpenChange: setControlledOpen,
  onOpenChangeCallback,
  open: controlledOpen,
  skipBodyWrapper = false,
  title,
  trigger,
}: SlideOverProps) {
  const [isUncontrolledOpen, setIsUncontrolledOpen] = React.useState(false)
  const labelId = React.useId()

  const open = controlledOpen ?? isUncontrolledOpen
  const setOpen = setControlledOpen ?? setIsUncontrolledOpen
  // Subscribe this component to the <FloatingTree> wrapper:
  const nodeId = useFloatingNodeId()

  function onOpenChange(nextOpen: boolean, event?: Event, reason?: OpenChangeReason) {
    setOpen(nextOpen, event, reason, FLOATING_TRANSITION_DURATION)
    onOpenChangeCallback?.({
      nextOpen,
      event,
      reason,
      transitionDuration: FLOATING_TRANSITION_DURATION,
    })
  }

  const {
    refs: { setReference, setFloating },
    context,
  } = useFloating({
    nodeId,
    open,
    onOpenChange,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' })
  const role = useRole(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])
  const { isMounted, status } = useTransitionStatus(context, { duration: FLOATING_TRANSITION_DURATION })

  let triggerWithExtraProps = trigger
  if (React.isValidElement(trigger)) {
    triggerWithExtraProps = React.cloneElement(trigger, {
      ref: setReference,
      type: 'button',
      ...getReferenceProps(),
    })
  }

  return (
    <>
      {triggerWithExtraProps}
      <FloatingNode id={nodeId}>
        {isMounted && (
          <FloatingPortal>
            <SlideOverOverlay data-status={status} lockScroll />
            <FloatingFocusManager context={context}>
              <SlideOverPanel
                aria-labelledby={labelId}
                aria-modal="true"
                data-status={status}
                ref={setFloating}
                {...getFloatingProps()}
              >
                <SlideOverHeader>
                  <Typography id={labelId} size="heading-4" tag="h2">
                    {title}
                  </Typography>
                  <IconButton
                    onClick={event => onOpenChange(false, event.nativeEvent)}
                    size="small"
                    variant="secondaryText"
                  >
                    <XmarkRegularIcon size={12} />
                  </IconButton>
                </SlideOverHeader>
                {skipBodyWrapper ? children : <SlideOverBody>{children}</SlideOverBody>}
                {(footerLeft || footerRight) && <SlideOverActions left={footerLeft} right={footerRight} />}
              </SlideOverPanel>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </>
  )
}

/**
 * A right-side sliding panel ("sidebar"), following the same floating-ui composition
 * pattern as `Popover`. Wraps the `FloatingTree` to manage the floating UI context if
 * this is a root component.
 */
export function SlideOver(props: SlideOverProps) {
  const parentId = useFloatingParentNodeId()

  if (parentId === null) {
    return (
      <FloatingTree>
        <SlideOverComponent {...props} />
      </FloatingTree>
    )
  }

  return <SlideOverComponent {...props} />
}
